import { useRef, useEffect, useCallback } from 'react';

import { PaymentIntent } from '@stripe/stripe-js';

import {
  useStripeExecuteMutation,
  StripeExecuteIntentRequest,
  StripeExecuteResponse,
} from '@/api/payments/stripeExecute';
import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { useClient } from '@/services/client/useClient';
import { TransactionState } from '@/features/paymentProcess/transactionSlice';
import { usePaymentsConfig } from '@/services/paymentsConfig/usePaymentsConfig';

const buildExecuteIntentPayload = (
  transactionState: TransactionState,
  client: string,
  accountIdentifier: string,
  paymentIntent: PaymentIntent,
): StripeExecuteIntentRequest => ({
  client,
  payment_intent_id: paymentIntent.id,
  transactionId: transactionState.transactionId,
  account_identifier: accountIdentifier,
});

export function useStripeExecuteIntent() {
  const onSuccess = useRef<((data: StripeExecuteResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  const transactionState = useAppSelector(state => state.transaction);

  const { getProviderStripeAccountIdentifier } = usePaymentsConfig();

  const stripeAccountIdentifier = getProviderStripeAccountIdentifier();

  const [stripeExecuteTrigger, {
    isLoading,
    data,
    error,
  }] = useStripeExecuteMutation();

  const { getClientId } = useClient();

  // Trigger payment-service execute intent
  const triggerExecuteIntentApi = useCallback(async (
    paymentIntent: PaymentIntent,
    callbacks?: {
      onSuccess?: (data: StripeExecuteResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;

    // Ensure paymentIntent is set
    if (!paymentIntent) {
      logger.error('Stripe paymentIntent not set');
      if (onError.current) {
        onError.current(new Error('Stripe paymentIntent not set'));
      }
      return;
    }

    // Require stripe provider config
    if (!stripeAccountIdentifier) {
      logger.error('Error accessing Stripe config');
      if (onError.current) {
        onError.current(new Error('Error accessing Stripe config'));
      }
      throw new Error('Error accessing Stripe config');
    }

    try {
      const payload = buildExecuteIntentPayload(
        transactionState,
        getClientId(),
        stripeAccountIdentifier,
        paymentIntent,
      );
      await stripeExecuteTrigger(payload);
    } catch (err) {
      logger.error('Error executing Stripe intent');
      logger.error(err);
      if (onError.current) {
        onError.current(err);
      }
    }
  }, [transactionState, getClientId, stripeAccountIdentifier, stripeExecuteTrigger]);

  // Handle payment-service execute intent complete
  useEffect(() => {
    // Wait until payment-service execute intent is complete
    if (isLoading || (!data && !error)) {
      return;
    }

    // Handle payment-service execute intent errors
    if (!!error || data?.message !== 'success') {
      logger.error('Error executing Stripe intent');
      logger.error(error);
      if (onError.current) {
        onError.current(error);
      }
      return;
    }

    if (onSuccess.current) {
      onSuccess.current(data);
    }
  }, [isLoading, data, error, onSuccess, onError]);

  return {
    triggerExecuteIntentApi,
    executeIntentIsLoading: isLoading,
    executeIntentData: data,
    executeIntentError: error,
  };
}
